import React, { useEffect, type MutableRefObject, type RefObject } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { RecursiveMenuTransitions } from '@uncinc/react-drupal-core';
import { useSubmenuContainerHeight } from '@uncinc/react-drupal-core/dist/Components/RecursiveMenu/menu-transitions';

import { Container, Lottie } from '@uncinc/uncinc-react-kitchen-sink';
import type { LottieItem } from '@uncinc/uncinc-react-kitchen-sink/dist/components/Lottie/types';

import hamburgerSearchLottie from '../../../../assets/lotties/hamburger-search.json';
import SoloSearch from '../../../../components/Search/SoloSearch';
import { SCIENTIFIC_DEPARTMENT_BLOCK_CLASSNAME } from '../../../../config/constants';
import Logo from '../../Logo';
import {
  ButtonWrapper,
  LanguageSwitcher,
  RecursiveMenu,
  RecursiveMenuMaxMenu,
} from '../../../../components';

import './style.scss';

interface MenuButtonProps {
  lottieRef: RefObject<LottieItem>;
  isOverlayOpen: MutableRefObject<boolean>;
}

const MenuButton: React.FC<MenuButtonProps> = React.memo(({
  lottieRef,
  isOverlayOpen,
}) => (
  <Lottie
    id="hamburgerOpen"
    ref={lottieRef}
    data={hamburgerSearchLottie}
    autoPlay={false}
    loop={false}
    direction={1}
    onInit={(instance: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      instance.addEventListener('complete', () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        instance.setDirection(isOverlayOpen.current ? -1 : 1);
      });
    }}
  />
));

interface HeaderProps {
  isOverlayOpen: MutableRefObject<boolean>;
  toggleOverlay: (arg0?: boolean) => void;
  headerRef: RefObject<HTMLDivElement>;
  lottieRef: RefObject<LottieItem>;
  searchInputRef: RefObject<HTMLInputElement>;
}

const Header: React.FC<HeaderProps> = ({
  isOverlayOpen,
  toggleOverlay,
  headerRef,
  lottieRef,
  searchInputRef,
}) => {
  const { t } = useTranslation();

  const collapseAll = RecursiveMenuTransitions.useCollapseSubmenus();
  const currentItem = RecursiveMenuTransitions.useExpandedSubmenu();
  const submenuHeight = useSubmenuContainerHeight();

  const menuButtonRef = React.useRef<HTMLAnchorElement>(null);

  // Set the custom scientific departments block height to the height of the submenu.
  useEffect(() => {
    const scientificDepartmentsBlock = document.querySelector(`.${SCIENTIFIC_DEPARTMENT_BLOCK_CLASSNAME}`) as HTMLLIElement;

    if (!scientificDepartmentsBlock) {
      return;
    }

    scientificDepartmentsBlock.style.height = `${submenuHeight}px`;
  }, [submenuHeight]);

  return (
    <div className="HeaderMenu__Header">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={classNames([
          'HeaderMenu__Header__overlay',
          `HeaderMenu__Header__overlay--${currentItem ? 'visible' : 'hidden'}`,
        ])}
        onClick={() => collapseAll()}
      />

      {/* Visual colored blocks on top of the header */}
      <div className="HeaderMenu__Header__blocks">
        {Array.from({ length: 5 }).map(() => (
          <span />
        ))}
      </div>

      <div className="HeaderMenu__Header__background">
        <Container className="HeaderMenu__Header__wrapper">
          <div className="row">
            <div className="col-2 col-md-1">
              <Logo
                className="HeaderMenu__Header__logo"
                toggleOverlay={collapseAll}
              />
            </div>

            <div className="col-10 col-md-11">
              <div className="HeaderMenu__Header__inner">
                <div className="HeaderMenu__Header__menus">
                  <div
                    ref={headerRef}
                    id="header-menu"
                    className="HeaderMenu__Header__menu"
                  >
                    <div className="HeaderMenu__Header__menu__inner">
                      <SoloSearch
                        toggleOverlay={toggleOverlay}
                      />

                      <RecursiveMenuMaxMenu
                        menuId="main"
                        className="HeaderMenu__Header__menu--main"
                        parentMenuItemWrapperClasses="col-12 col-md-5"
                        menuItemWrapperWrapperClasses="col-12 col-md-7"
                        linkIconName="chevron_right"
                        withHeading
                        navProps={{ 'aria-label': t('recursive_menu.main.aria_label') }}
                        onClickFunction={() => {
                          toggleOverlay(true);
                          collapseAll();
                        }}
                      />

                      <RecursiveMenu
                        menuId="secondary"
                        className="HeaderMenu__Header__menu--secondary"
                        maxLevel={1}
                        linkIconName="chevron_right"
                        navProps={{ 'aria-label': t('recursive_menu.secondary.aria_label') }}
                        onClickFunction={() => {
                          toggleOverlay(true);
                          collapseAll();
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="HeaderMenu__Header__actions">
                  <SoloSearch collapsible {...{ menuButtonRef, searchInputRef }} />

                  <LanguageSwitcher buttonRef={menuButtonRef} />

                  <ButtonWrapper
                    className="HeaderMenu__Header__hamburger"
                    tabIndex={0}
                    type="textual"
                    aria-expanded={isOverlayOpen.current}
                    aria-controls="header-menu"
                    aria-label={t(`menu.hamburger.aria_label_${isOverlayOpen.current ? 'close' : 'open'}`)}
                    onClick={() => {
                      toggleOverlay();

                      // Collapse the active MaxMenu submenu if it is expanded.
                      if (currentItem !== null) {
                        collapseAll();
                      }
                    }}
                  >
                    <div className="HeaderMenu__Header__hamburger__label">
                      <span className="HeaderMenu__Header__hamburger__label__state">{t('general.close')}</span>
                      <span className="HeaderMenu__Header__hamburger__label__state">{t('general.menu')}</span>
                    </div>
                    <div className="HeaderMenu__Header__hamburger__button">
                      <MenuButton
                        lottieRef={lottieRef}
                        isOverlayOpen={isOverlayOpen}
                      />
                    </div>
                  </ButtonWrapper>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default Header;
